import { useAppStore } from '@/store/useAppStore';
import { useOrganizationOnboardingStore } from '@/store/useOrganizationOnboardingStore';
import { useScreenerStore } from '@/store/useScreenerStore';
import { useUserOnboardingStore } from '@/store/useUserOnboardingStore';
import { redirect } from '@tanstack/react-router';
import axios from 'axios';

export type TCustomError = {
  message: string;
  code: number;
  name: string;
  payload: null;
};

export type TCustomAxiosError = {
  code: number;
  data: null;
  success: boolean;
  errors: TCustomError[];
};

export type TCustomAxiosResponse<T> = {
  code: number;
  success: boolean;
  errors: TCustomError[];
  data: T;
  pagination?: {
    page: number;
    limit: number;
    totalPages: number;
    offset: number;
    totalItems: number;
  };
};

const api = axios.create({
  baseURL: import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let retryCount = 0;
const maxRetries = 3;
let lastRetryTime = 0;
const RETRY_RESET_TIME = 5 * 60 * 1000; // 5 minutes

api.interceptors.response.use(
  (response) => {
    retryCount = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Reset retry count if enough time has passed
    if (Date.now() - lastRetryTime > RETRY_RESET_TIME) {
      retryCount = 0;
    }

    // Only refresh on 401 (expired tokens), not 403 (business logic), and exclude login endpoints
    if (
      error.response?.status === 401 &&
      retryCount < maxRetries &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/login')
    ) {
      originalRequest._retry = true;
      retryCount += 1;
      lastRetryTime = Date.now();

      try {
        await api.post('/v1/user/auth/refresh');
        return api(originalRequest);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (refreshError: any) {
        retryCount = maxRetries;
        signOutRedirectAndStoreReset(refreshError);
        return Promise.reject(refreshError);
      }
    }

    // Handle max retries reached
    if (retryCount >= maxRetries) {
      signOutRedirectAndStoreReset(error);
    }

    return Promise.reject(error);
  },
);

export { api };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const signOutRedirectAndStoreReset = (error: any) => {
  const appStore = useAppStore.getState();
  const userOnboardingStore = useUserOnboardingStore.getState();
  const organizationOnboardingStore = useOrganizationOnboardingStore.getState();
  const screenerStore = useScreenerStore.getState();

  appStore.reset();
  userOnboardingStore.reset();
  organizationOnboardingStore.reset();
  screenerStore.reset();

  const message = error.response?.data?.errors?.[0]?.message || error.message;
  const isInvalidCredentials = message?.includes('credentials');

  if (!isInvalidCredentials) {
    redirect({
      to: '/sign-in',
      replace: true,
    });
    window.location.reload();
  }
};
